import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";

interface User {
	id: string;
	first_name: string | null;
	last_name: string | null;
	email: string | null;
	avatar_url: string | null;
	role: string | null;
	active: boolean;
}

export const useUsers = () => {
	const { currentOrganization } = useAuth();
	const organizationUUID = currentOrganization?.id;
	
	return useQuery({
		queryKey: ["users", organizationUUID],
		queryFn: async () => {
			if (!organizationUUID) return [];
			
			// Get all users in the current organization
			const { data: userOrgs, error: userOrgsError } = await supabase
				.from("user_organization")
				.select("user_uuid")
				.eq("organization_uuid", organizationUUID);
			
			if (userOrgsError) throw userOrgsError;
			
			const userIds = userOrgs.map((uo) => uo.user_uuid);
			
			if (userIds.length === 0) return [];
			
			// Get profile information for these users
			const { data: profiles, error: profilesError } = await supabase
				.from("profiles")
				.select("id, first_name, last_name, email, avatar_url, role, active")
				.in("id", userIds)
				.eq("active", true)
				.order("first_name", { ascending: true });
			
			if (profilesError) throw profilesError;
			
			return profiles as User[];
		},
		enabled: !!organizationUUID,
	});
};
